import { useContext, useEffect, useState } from 'react';
import triggerTracking from '@blueprint/tracking';
import {
    OptimizelyDecision,
    useDecision,
    useExperiment
} from '@optimizely/react-sdk';

import {
    OptimizelyOverrides,
    OptimizelyOverridesContext
} from '../optimizelyOverrides';

type Config = { disableTracking?: boolean };

const useClientLoader = (isOptimizelyClientReady: boolean) => {
    const [isMounted, setIsMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isLoading) return;
        const isLoaded = isMounted && isOptimizelyClientReady;
        setIsLoading(!isLoaded);
    }, [isMounted, isOptimizelyClientReady, isLoading]);

    return isLoading;
};

type EventData =
    | { type: 'experiment'; name: string; variation?: string | null }
    | { type: 'flag'; name: string; variation?: boolean };

const useTrackGAEvent = (
    { type, name, variation }: EventData,
    trackingDisabled = false
) => {
    const eventCategory = {
        experiment: 'optimizely',
        flag: 'optimizely_feature'
    }[type];
    const eventLabel = variation?.toString();
    const eventAction = name;
    const taxonomy = {
        optimizely_experiment: name,
        optimizely_variation: eventLabel
    };

    useEffect(() => {
        if (!trackingDisabled) {
            if (variation !== undefined && variation !== null) {
                triggerTracking<{
                    taxonomy: {
                        optimizely_experiment: string;
                        optimizely_variation: string | undefined;
                    };
                }>({
                    event: 'GAEvent',
                    eventCategory,
                    eventLabel,
                    eventAction,
                    taxonomy
                });
            }
        }
    }, []); //eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Returns state of Optimizely flag, wrapped with custom overrides logic
 * Will emit a GA Event for tracking on client load
 * OptimizelyDecision docs: https://docs.developers.optimizely.com/full-stack/v4.0/docs/optimizelydecision-react
 */
export const useOptimizelyFeatureFlag = (
    featureFlagName: string,
    config: Config = { disableTracking: false }
): [
    isEnabled: boolean,
    decision: OptimizelyDecision,
    isOptimizelyClientReady: boolean
] => {
    const { features: featureFlagOverrides } =
        useContext(OptimizelyOverridesContext) || {};
    const [decision, isOptimizelyClientReady] = useDecision(featureFlagName);
    const hasOverride =
        featureFlagOverrides && featureFlagName in featureFlagOverrides;
    /* note that the ts version used by linter does not correctly infer the
    featureFlagOverrides type when abstracting hasOverride to a const instead of
    inline so we have to typecast it to avoid linting errors below */

    useTrackGAEvent(
        {
            type: 'flag',
            variation: hasOverride
                ? (featureFlagOverrides as OptimizelyOverrides['features'])[
                      featureFlagName
                  ]
                : decision.enabled,
            name: featureFlagName
        },
        config.disableTracking
    );

    /**
     * Todo: there is no way to natively force features with Optimizely, so we
     * always have to return the forced state if it exists
     */
    if (hasOverride) {
        const enabled = (
            featureFlagOverrides as OptimizelyOverrides['features']
        )[featureFlagName];
        return [
            enabled,
            {
                ...decision,
                variationKey: null, // SDK did not make the decision so its the same as failing
                ruleKey: null, // flag rules are not invoked as we override
                enabled,
                reasons: ['Forced with override']
            },
            isOptimizelyClientReady
        ];
    }

    return [decision.enabled, decision, isOptimizelyClientReady];
};

export const useLazyOptimizelyFeatureFlag = (
    featureFlagName: string,
    config?: Config
): [
    isLoading: boolean,
    isEnabled: boolean | null,
    decision: OptimizelyDecision | null,
    isOptimizelyClientReady: boolean
] => {
    const [isFeatureEnabled, decision, isOptimizelyClientReady] =
        useOptimizelyFeatureFlag(featureFlagName, config);
    const isLoading = useClientLoader(isOptimizelyClientReady);
    if (isLoading) return [isLoading, null, null, isOptimizelyClientReady];
    return [isLoading, isFeatureEnabled, decision, isOptimizelyClientReady];
};

/**
 * @deprecated in favour of standard event tracking out of the box with {@link useOptimizelyFeatureFlag}
 *
 * Wraps {@link useOptimizelyFeatureFlag} with customisable GA event formatting
 */
export const useCustomTrackOptimizelyFeatureFlag = (
    feature: string,
    featureEnabledLabel = 'alternative',
    featureDisabledLabel = 'control'
): ReturnType<typeof useOptimizelyFeatureFlag> => {
    const [isFeatureEnabled, decision, isOptimizelyClientReady] =
        useOptimizelyFeatureFlag(feature, { disableTracking: true });

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely_feature',
            eventAction: feature.toString(),
            eventLabel: isFeatureEnabled
                ? featureEnabledLabel
                : featureDisabledLabel
        });
    }, [feature, featureEnabledLabel, isFeatureEnabled, featureDisabledLabel]);

    return [isFeatureEnabled, decision, isOptimizelyClientReady];
};

/**
 * Activates and returns state of Optimizely experiment, wrapped with custom
 * overrides logic
 */
export const useOptimizelyExperiment = (
    experimentKey: string,
    config: Config = { disableTracking: false }
): [variation: string | null, isOptimizelyClientReady: boolean] => {
    const { experiments: experimentOverrides } =
        useContext(OptimizelyOverridesContext) || {};
    const [variation, isOptimizelyClientReady] = useExperiment(experimentKey);
    const hasOverride =
        experimentOverrides && experimentKey in experimentOverrides;
    /* note that the ts version used by linter does not correctly infer the
    experimentOverrides type when abstracting hasOverride to a const instead of
    inline so we have to typecast it to avoid linting errors below */

    useTrackGAEvent(
        {
            type: 'experiment',
            variation: hasOverride
                ? (experimentOverrides as OptimizelyOverrides['experiments'])[
                      experimentKey
                  ]
                : variation,
            name: experimentKey
        },
        config.disableTracking
    );

    if (hasOverride) {
        return [
            (experimentOverrides as OptimizelyOverrides['experiments'])[
                experimentKey
            ],
            isOptimizelyClientReady
        ];
    }

    return [variation, isOptimizelyClientReady];
};

export const useLazyOptimizelyExperiment = (
    experimentKey: string,
    config?: Config
): [
    isLoading: boolean,
    variation: string | null,
    isOptimizelyClientReady: boolean
] => {
    const [variation, isOptimizelyClientReady] = useOptimizelyExperiment(
        experimentKey,
        config
    );
    const isLoading = useClientLoader(isOptimizelyClientReady);
    if (isLoading) return [isLoading, null, isOptimizelyClientReady];
    return [isLoading, variation, isOptimizelyClientReady];
};
